import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, MapPin, AlertCircle, Repeat, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

// Optimized Leaflet Map Component
const LeafletMap = ({ center, zoom, className, style }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadLeaflet = async () => {
      if (typeof window.L !== 'undefined') {
        initializeMap();
        return;
      }

      if (window.leafletLoading) {
        window.leafletLoading.then(() => initializeMap());
        return;
      }

      window.leafletLoading = new Promise((resolve) => {
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(cssLink);
        }

        if (!document.querySelector('script[src*="leaflet.js"]')) {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = () => {
            resolve();
            initializeMap();
          };
          document.head.appendChild(script);
        } else {
          resolve();
          initializeMap();
        }
      });
    };

    const initializeMap = () => {
      if (mapRef.current && !mapInstanceRef.current && window.L) {
        try {
          mapInstanceRef.current = window.L.map(mapRef.current, {
            zoomControl: false,
            attributionControl: false
          }).setView(center, zoom);

          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19
          }).addTo(mapInstanceRef.current);

          markerRef.current = window.L.marker(center).addTo(mapInstanceRef.current);
          setIsLoaded(true);
        } catch (error) {
          console.error('Map initialization error:', error);
        }
      }
    };

    loadLeaflet();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current && center && isLoaded) {
      mapInstanceRef.current.setView(center, zoom);
      if (markerRef.current) {
        markerRef.current.setLatLng(center);
      }
    }
  }, [center, zoom, isLoaded]);

  return <div ref={mapRef} className={className} style={style}></div>;
};

const PhotoCapture = ({ onPhotosChange, required = true, resetTrigger, projectId }) => {
  const [photos, setPhotos] = useState([]);
  const [stream, setStream] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [locationPermissionStatus, setLocationPermissionStatus] = useState('prompt');
  const [cameraPermissionStatus, setCameraPermissionStatus] = useState('prompt');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const videoRef = useRef(null);
  const locationCache = useRef(new Map());

  // Check permissions on component mount
  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    try {
      // Check location permission
      if ('permissions' in navigator) {
        const locationPermission = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermissionStatus(locationPermission.state);
        
        locationPermission.onchange = () => {
          setLocationPermissionStatus(locationPermission.state);
        };
      }

      // Check camera permission
      if ('permissions' in navigator) {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        setCameraPermissionStatus(cameraPermission.state);
        
        cameraPermission.onchange = () => {
          setCameraPermissionStatus(cameraPermission.state);
        };
      }
    } catch (err) {
      console.log('Permissions API not fully supported');
    }
  };

  const resetPhotos = () => {
    setPhotos([]);
    setCurrentLocation(null);
    setLocationName('');
    setError(null);
    stopCamera();
    
    if (onPhotosChange) {
      onPhotosChange([]);
    }
  };

  useEffect(() => {
    if (resetTrigger) {
      resetPhotos();
    }
  }, [resetTrigger]);

  const checkForMultipleCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    } catch (err) {
      console.error('Error checking cameras:', err);
    }
  };

  const requestLocationPermission = async () => {
    setIsGettingLocation(true);
    setError(null);
    
    try {
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      setLocationPermissionStatus('granted');
      
      // Get location name
      const placeName = await getLocationName(location.lat, location.lng);
      setLocationName(placeName);
      
    } catch (error) {
      console.error('Location error:', error);
      setLocationPermissionStatus('denied');
      
      if (error.code === 1) { // PERMISSION_DENIED
        setError('Location access denied. Please enable location in your browser settings and refresh the page.');
      } else if (error.code === 2) { // POSITION_UNAVAILABLE
        setError('Location unavailable. Please check your GPS/location services.');
      } else if (error.code === 3) { // TIMEOUT
        setError('Location request timed out. Please try again.');
      } else {
        setError('Unable to get location. Please enable location services.');
      }
    } finally {
      setIsGettingLocation(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout for mobile
        maximumAge: 60000 // Cache for 1 minute
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => reject(error),
        options
      );
    });
  };

  const getLocationName = async (lat, lng) => {
    try {
      const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      if (locationCache.current.has(cacheKey)) {
        return locationCache.current.get(cacheKey);
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1&accept-language=en`,
        {
          headers: {
            'User-Agent': 'InspectionApp/1.0'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('API request failed');
      }
      
      const data = await response.json();
      
      let placeName = '';
      if (data.address) {
        const address = data.address;
        const addressParts = [];
        
        if (address.road) addressParts.push(address.road);
        if (address.suburb || address.neighbourhood) addressParts.push(address.suburb || address.neighbourhood);
        if (address.city || address.town) addressParts.push(address.city || address.town);
        
        placeName = addressParts.slice(0, 2).join(', ') || 'Unknown Location';
      } else {
        placeName = 'Unknown Location';
      }
      
      locationCache.current.set(cacheKey, placeName);
      return placeName;
    } catch (error) {
      console.error('Error fetching location name:', error);
      return 'Unknown Location';
    }
  };

  const addWatermark = async (canvas, location, placeName) => {
    const ctx = canvas.getContext('2d');
    const timestamp = new Date().toLocaleString();
    
    // Make watermark more visible on mobile
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    const watermarkHeight = Math.max(80, canvas.height * 0.12); // Responsive height
    ctx.fillRect(0, canvas.height - watermarkHeight, canvas.width, watermarkHeight);
    
    ctx.fillStyle = 'white';
    const fontSize = Math.max(12, canvas.width * 0.025); // Responsive font size
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillText(placeName, 10, canvas.height - watermarkHeight + 20);
    
    const smallFontSize = Math.max(10, canvas.width * 0.02);
    ctx.font = `${smallFontSize}px Arial`;
    ctx.fillText(`Lat: ${location.lat.toFixed(6)}°  Lng: ${location.lng.toFixed(6)}°`, 10, canvas.height - watermarkHeight + 40);
    ctx.fillText(`Captured: ${timestamp}`, 10, canvas.height - watermarkHeight + 55);
    ctx.fillText(`Accuracy: ±${Math.round(location.accuracy)}m`, 10, canvas.height - watermarkHeight + 70);
    
    return canvas;
  };

  const uploadImageToAPI = async (canvas) => {
    try {
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.85); // Slightly better quality
      });

      const formData = new FormData();
      formData.append('image', blob, `inspection_${Date.now()}.jpg`);
      
      if (projectId) {
        formData.append('project_id', projectId);
      }

      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/project/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        return data.file;
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const startCamera = async () => {
    try {
      setError(null);
      checkForMultipleCameras();
      
      // Enhanced camera constraints for mobile
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280, min: 640, max: 1920 },
          height: { ideal: 720, min: 480, max: 1080 },
          frameRate: { ideal: 30, min: 15, max: 60 }
        },
        audio: false
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setStream(mediaStream);
      setShowCamera(true);
      setCameraPermissionStatus('granted');
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        
        // Better mobile video handling
        videoRef.current.setAttribute('playsinline', true);
        videoRef.current.setAttribute('webkit-playsinline', true);
        
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Video play failed:', error);
            setError('Failed to start camera preview');
          });
        }
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraPermissionStatus('denied');
      
      if (err.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is being used by another application.');
      } else {
        setError('Unable to access camera. Please try again.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  const flipCamera = async () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);
    
    try {
      const constraints = {
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 }
        },
        audio: false
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      setStream(mediaStream);
    } catch (err) {
      console.error('Error flipping camera:', err);
      setError('Unable to switch camera.');
    }
  };

  const capturePhoto = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // Ensure we have location before capturing
      let location = currentLocation;
      if (!location) {
        location = await getCurrentLocation();
        setCurrentLocation(location);
      }
      
      let placeName = locationName;
      if (!placeName) {
        placeName = await getLocationName(location.lat, location.lng);
        setLocationName(placeName);
      }
      
      const video = videoRef.current;
      if (!video || video.readyState !== 4) {
        throw new Error('Camera not ready');
      }
      
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      await addWatermark(canvas, location, placeName);
      
      const imagePath = await uploadImageToAPI(canvas);
      const previewUrl = canvas.toDataURL('image/jpeg', 0.8);
      
      const newPhoto = {
        id: Date.now(),
        url: previewUrl,
        path: imagePath,
        location: location,
        placeName: placeName,
        timestamp: new Date().toISOString()
      };
      
      const updatedPhotos = [...photos, newPhoto];
      if (updatedPhotos.length <= 3) {
        setPhotos(updatedPhotos);
        stopCamera();
        
        if (onPhotosChange) {
          onPhotosChange(updatedPhotos);
        }
      } else {
        setError('Maximum 3 photos allowed');
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      if (error.code === 1) {
        setError('Location permission denied. Please enable location services.');
      } else if (error.message && error.message.includes('Upload failed')) {
        setError('Failed to upload image. Please check your internet connection.');
      } else {
        setError('Failed to capture photo. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const removePhoto = (photoId) => {
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    setPhotos(updatedPhotos);
    
    if (onPhotosChange) {
      onPhotosChange(updatedPhotos);
    }
    
    if (updatedPhotos.length === 0) {
      setCurrentLocation(null);
      setLocationName('');
    }
  };

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Mobile-optimized layout
  return (
    <div className="max-w-4xl mx-auto space-y-4 p-2 md:p-0">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Permission Status Cards */}
      {(locationPermissionStatus === 'denied' || cameraPermissionStatus === 'denied') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 mb-2">Permissions Required</h4>
          <div className="space-y-2 text-sm">
            {locationPermissionStatus === 'denied' && (
              <div className="flex items-center justify-between">
                <span className="text-yellow-700">📍 Location access needed</span>
                <button 
                  onClick={requestLocationPermission}
                  disabled={isGettingLocation}
                  className="text-blue-600 underline disabled:opacity-50"
                >
                  {isGettingLocation ? 'Getting...' : 'Enable'}
                </button>
              </div>
            )}
            {cameraPermissionStatus === 'denied' && (
              <div className="flex items-center justify-between">
                <span className="text-yellow-700">📷 Camera access needed</span>
                <button 
                  onClick={startCamera}
                  className="text-blue-600 underline"
                >
                  Enable
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-yellow-600 mt-2">
            If permissions are blocked, please reset them in your browser settings and refresh the page.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Photo Capture Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-medium text-gray-700 flex items-center">
                <Camera className="h-4 w-4 text-blue-600 mr-2" />
                Location Photos
              </h3>
              <span className="text-xs text-gray-500">
                {photos.length}/3
              </span>
            </div>

            {/* Location Check */}
            {!currentLocation && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700 mb-2">Location required for photo capture</p>
                <button
                  onClick={requestLocationPermission}
                  disabled={isGettingLocation}
                  className="w-full py-2 px-3 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isGettingLocation ? (
                    <>
                      <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <MapPin className="h-4 w-4" />
                      Enable Location
                    </>
                  )}
                </button>
              </div>
            )}

            {!showCamera ? (
              <>
                {photos.length < 3 && currentLocation && (
                  <button
                    onClick={startCamera}
                    disabled={isProcessing}
                    className="w-full mb-3 py-3 px-3 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Camera className="h-4 w-4" />
                    Take Photo
                  </button>
                )}
              </>
            ) : (
              <div className="mb-3">
                <div className="relative aspect-video bg-black rounded mb-3">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover rounded"
                    style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                  />
                  {hasMultipleCameras && (
                    <button
                      onClick={flipCamera}
                      disabled={isProcessing}
                      className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
                    >
                      <Repeat className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={capturePhoto}
                    disabled={photos.length >= 3 || isProcessing || !currentLocation}
                    className="flex-1 px-4 py-3 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4" />
                        Capture
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={stopCamera}
                    disabled={isProcessing}
                    className="px-4 py-3 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Photo Preview */}
            {photos.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-2">Captured Photos</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative aspect-square">
                      <img
                        src={photo.url}
                        alt="Captured"
                        className="w-full h-full object-cover rounded border"
                      />
                      <button
                        onClick={() => removePhoto(photo.id)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Map and Location Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4">
            <h3 className="text-base font-medium text-gray-700 flex items-center mb-3">
              <MapPin className="h-4 w-4 text-blue-600 mr-2" />
              Location Details
            </h3>
            
            {currentLocation ? (
              <div className="space-y-3">
                <div className="h-32 md:h-40">
                  <LeafletMap
                    center={[currentLocation.lat, currentLocation.lng]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                    className="rounded border"
                  />
                </div>
                
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="font-medium text-sm">{locationName || 'Loading location...'}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>Lat: {currentLocation.lat.toFixed(6)}°</div>
                    <div>Lng: {currentLocation.lng.toFixed(6)}°</div>
                  </div>
                  <div>Accuracy: ±{Math.round(currentLocation.accuracy)}m</div>
                </div>
              </div>
            ) : (
              <div className="h-32 md:h-40 flex items-center justify-center border rounded text-gray-400 text-sm">
                Enable location to view map
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-sm">
<div className={`w-2 h-2 rounded-full ${photos.length >= 1 ? 'bg-green-500' : 'bg-red-500'}`}></div>
        <span className="text-blue-800">
  Photos: {photos.length}/3 (minimum 1 required)
</span>
        </div>
      </div>
    </div>
  );
};

export default PhotoCapture;